import React, { useState, useEffect } from 'react';
import { Sidebar } from '../dashboard_component';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Modal } from 'react-bootstrap';
import MyTextEditor from './MyTextEditor';

// Import centralized API
import { cbtAPI, assetAPI } from '../../api';

const CbtQuestionEdit = () => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const navigate = useNavigate();

    // URL parameters
    const urlParam = new URLSearchParams(window.location.search);
    const cbtDataPasscode = urlParam.get("cbt_data_id");
    const cbtQuestionPasscode = urlParam.get("cbt_question_id");

    // -------------------- Question State --------------------
    const [questionData, setQuestionData] = useState({
        question: "",
        question_type: "multiple_choice",
        question_format: "text",
        difficulty: "medium",
        topic: "",
        sub_topic: "",
        score: 1,
        max_options_select: 1,
        partial_scoring: false,
        negative_marking: false,
        mandatory: false,
        has_attachment: false,
        sort: 1,
        display_sort: true,
        status: "draft",
        explanation: "",
        reference: "",
        answers: [],
        cbt_question_id: "",
        correct_answer: "", // for true/false and fill_blank
        section_id: null,
        custom_mark: null,
    });

    // -------------------- Answer State --------------------
    const [answers, setAnswers] = useState([]);
    const [newAnswer, setNewAnswer] = useState({
        options: "",
        answer_text: null,
        option_status: "options",
        option_label: "",
        partial_score: 0,
        sort_order: 0,
        match_type: "exact",
        true_false_value: null,
        match_pair: "",
        order_position: null,
    });
    const [editingAnswer, setEditingAnswer] = useState(null);
    const [currentAnswer, setCurrentAnswer] = useState(null);

    // -------------------- UI State --------------------
    const [dbImgFiles, setDbImgFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [showAnswerModal, setShowAnswerModal] = useState(false);
    const [showDeleteAnswerModal, setShowDeleteAnswerModal] = useState(false);

    const handleToggle = () => setIsCollapsed(!isCollapsed);

    // -------------------- Fetch Question Data --------------------
    const fetchQuestionData = async () => {
        try {
            setLoading(true);
            const response = await cbtAPI.getQuestions(cbtDataPasscode);

            if (response.data && response.data.data) {
                const question = response.data.data.find(
                    (q) => q.cbt_question_id === cbtQuestionPasscode
                );
                if (question) {
                    // Extract correct answer for true/false and fill_blank
                    const correctAnswer = (() => {
                        if (question.question_type === 'true_false' || question.question_type === 'fill_blank') {
                            const correctAns = (question.answers || []).find(
                                a => a.option_status === 'correct' || a.is_correct === true
                            );
                            if (correctAns) {
                                return question.question_type === 'true_false'
                                    ? (correctAns.true_false_value ? 'true' : 'false')
                                    : correctAns.answer_text || '';
                            }
                        }
                        return "";
                    })();

                    setQuestionData({
                        cbt_question_id: question.cbt_question_id,
                        question: question.question || "",
                        question_type: question.question_type || "multiple_choice",
                        question_format: question.question_format || "text",
                        difficulty: question.difficulty || "medium",
                        topic: question.topic || "",
                        sub_topic: question.sub_topic || "",
                        score: question.score || 1,
                        max_options_select: question.max_options_select || 1,
                        partial_scoring: question.partial_scoring || false,
                        negative_marking: question.negative_marking || false,
                        mandatory: question.mandatory || false,
                        has_attachment: question.has_attachment || false,
                        sort: question.sort || 1,
                        display_sort: question.display_sort !== undefined ? question.display_sort : true,
                        status: question.status || "draft",
                        explanation: question.explanation || "",
                        reference: question.reference || "",
                        answers: question.answers || [],
                        correct_answer: correctAnswer,
                        section_id: question.section_id || null,
                        custom_mark: question.custom_mark || null,
                    });
                    setAnswers(question.answers || []);
                } else {
                    toast.error('Question not found');
                    navigate(`/cbt_creator/cbt_data?cbt_data_id=${cbtDataPasscode}`);
                }
            }
        } catch (error) {
            console.error('Fetch question error:', error);
            toast.error('Failed to load question data');
        } finally {
            setLoading(false);
        }
    };

    // -------------------- Fetch Image Library --------------------
    const fetchImages = async () => {
        try {
            const response = await assetAPI.getImageLibrary();
            setDbImgFiles(response.data || []);
        } catch (error) {
            toast.error('Failed to load images');
        }
    };

    useEffect(() => {
        fetchQuestionData();
        fetchImages();
    }, []);

    // -------------------- Question Update --------------------
    const handleQuestionSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            const updateData = {
                question: questionData.question,
                question_type: questionData.question_type,
                question_format: questionData.question_format,
                difficulty: questionData.difficulty,
                topic: questionData.topic || null,
                sub_topic: questionData.sub_topic || null,
                explanation: questionData.explanation || null,
                reference: questionData.reference || null,
                score: parseFloat(questionData.score),
                max_options_select: parseInt(questionData.max_options_select) || 1,
                partial_scoring: questionData.partial_scoring,
                negative_marking: questionData.negative_marking,
                mandatory: questionData.mandatory,
                has_attachment: questionData.has_attachment,
                sort: parseInt(questionData.sort) || 1,
                display_sort: questionData.display_sort,
                status: questionData.status,
            };

            // Include correct_answer for true/false and fill_blank
            if (['true_false', 'fill_blank'].includes(questionData.question_type)) {
                updateData.correct_answer = questionData.correct_answer;
            }

            const response = await cbtAPI.updateQuestion(
                cbtDataPasscode,
                cbtQuestionPasscode,
                updateData
            );

            if (response.success || response.data) {
                toast.success('Question updated successfully!');
                setShowConfigModal(false);
                await fetchQuestionData();
            } else {
                throw new Error(response.error || 'Failed to update question');
            }
        } catch (error) {
            console.error('Update question error:', error);
            toast.error(error.response?.data?.detail || error.message || 'Failed to update question');
        } finally {
            setSaving(false);
        }
    };

    // -------------------- Answer Create / Update --------------------
    const handleAnswerSubmit = async () => {
        const hasContent = newAnswer.options?.trim() || newAnswer.answer_text?.trim();
        if (!hasContent) {
            toast.error('Answer content cannot be empty');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                cbt_question_id: cbtQuestionPasscode,
                options: newAnswer.options || null,
                answer_text: newAnswer.answer_text || null,
                option_status: newAnswer.option_status,
                match_type: newAnswer.match_type,
                true_false_value: newAnswer.true_false_value,
                partial_score: parseFloat(newAnswer.partial_score) || 0,
                option_label: newAnswer.option_label || null,
                match_pair: newAnswer.match_pair || null,
                order_position: newAnswer.order_position ? parseInt(newAnswer.order_position) : null,
                sort_order: parseInt(newAnswer.sort_order) || 0,
            };

            if (editingAnswer) {
                await cbtAPI.updateAnswer(editingAnswer.answer_id, payload);
                toast.success('Answer updated successfully');
            } else {
                await cbtAPI.createAnswer(payload);
                toast.success('Answer added successfully');
            }

            await fetchQuestionData();
            setShowAnswerModal(false);
            resetAnswerForm();
        } catch (error) {
            console.error('Save answer error:', error);
            toast.error(error.response?.data?.detail || 'Failed to save answer');
        } finally {
            setSaving(false);
        }
    };

    // -------------------- Answer Delete --------------------
    const handleDeleteAnswer = async (answerId) => {
        try {
            await cbtAPI.deleteAnswer(answerId);
            toast.success('Answer deleted successfully');
            await fetchQuestionData();
            setShowDeleteAnswerModal(false);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete answer');
        }
    };

    // -------------------- Reset Answer Form --------------------
    const resetAnswerForm = () => {
        setNewAnswer({
            options: "",
            answer_text: null,
            option_status: "options",
            option_label: "",
            partial_score: 0,
            sort_order: 0,
            match_type: "exact",
            true_false_value: null,
            match_pair: "",
            order_position: null,
        });
        setEditingAnswer(null);
    };

    // -------------------- Helper: Map Question Type --------------------
    const getQuestionTypeDisplay = (type) => {
        const typeMap = {
            'multiple_choice': 'Multiple Choice',
            'single_choice': 'Single Choice',
            'essay': 'Essay',
            'theory': 'Theory',
            'true_false': 'True/False',
            'fill_blank': 'Fill in the Blank',
            'matching': 'Matching',
            'ordering': 'Ordering',
            'file_upload': 'File Upload',
            'composite': 'Composite',
        };
        return typeMap[type] || type || 'Multiple Choice';
    };

    // -------------------- Render --------------------
    return (
        <div className="flex min-h-screen bg-gray-50">
            <Sidebar isCollapsed={isCollapsed} onToggle={handleToggle} />

            <div className={`flex-1 transition-all duration-300 pt-16 lg:pt-5 ${isCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

                    {/* Header */}
                    <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl shadow-xl overflow-hidden mb-8">
                        <div className="p-6 md:p-8 text-white">
                            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                                <div className="flex-1">
                                    <h1 className="text-2xl md:text-3xl font-bold mb-2 flex items-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                        Edit Question
                                    </h1>
                                    <p className="text-blue-100 text-sm md:text-base">
                                        Modify question content, options, and settings
                                        <span className="ml-4 text-sm">
                                            ID: <code className="bg-white bg-opacity-20 px-2 py-1 rounded">{cbtQuestionPasscode}</code>
                                        </span>
                                    </p>
                                </div>
                                <div className="flex flex-wrap gap-3">
                                    <button
                                        className="bg-white text-blue-600 hover:bg-blue-50 px-4 py-2 rounded-xl font-medium flex items-center gap-2 transition-all duration-200 hover:scale-105"
                                        onClick={() => setShowConfigModal(true)}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                                        </svg>
                                        Settings
                                    </button>
                                    {['multiple_choice', 'single_choice', 'true_false', 'fill_blank', 'matching', 'ordering'].includes(questionData.question_type) && (
                                        <button
                                            className="bg-white text-green-600 hover:bg-green-50 px-4 py-2 rounded-xl font-medium flex items-center gap-2 transition-all duration-200 hover:scale-105"
                                            onClick={() => {
                                                resetAnswerForm();
                                                setShowAnswerModal(true);
                                            }}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                                            </svg>
                                            Add Answer
                                        </button>
                                    )}
                                    <button
                                        className="bg-white text-purple-600 hover:bg-purple-50 px-4 py-2 rounded-xl font-medium flex items-center gap-2 transition-all duration-200 hover:scale-105"
                                        onClick={() => navigate(`/cbt_creator/cbt_data?cbt_data_id=${cbtDataPasscode}`)}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
                                        </svg>
                                        Back to Questions
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Loading State */}
                    {loading ? (
                        <div className="text-center py-16">
                            <svg className="animate-spin h-12 w-12 text-blue-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <p className="mt-4 text-gray-600">Loading question data...</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                            {/* Main Column: Question Editor & Answers */}
                            <div className="xl:col-span-2 space-y-8">

                                {/* Question Editor Card */}
                                <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
                                    <div className="p-6 border-b border-gray-100">
                                        <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            Question Content
                                        </h2>
                                    </div>
                                    <div className="p-6">
                                        <form onSubmit={handleQuestionSubmit}>
                                            {/* Rich Text Editor */}
                                            <div className="mb-6">
                                                <MyTextEditor
                                                    initialContent={questionData.question}
                                                    onChange={(value) => setQuestionData({ ...questionData, question: value })}
                                                    imageLibrary={dbImgFiles}
                                                    placeholder="Enter your question here..."
                                                />
                                            </div>

                                            {/* Question Type Display (read‑only) */}
                                            <div className="mb-6">
                                                <label className="block text-gray-700 font-semibold mb-3">Question Type</label>
                                                <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                                                    <span className="text-lg font-medium text-gray-800">
                                                        {getQuestionTypeDisplay(questionData.question_type)}
                                                    </span>
                                                    <p className="text-sm text-gray-500 mt-1">
                                                        {questionData.question_type === 'multiple_choice' && 'Students can select multiple correct options'}
                                                        {questionData.question_type === 'single_choice' && 'Students select one correct answer'}
                                                        {questionData.question_type === 'true_false' && 'Students choose True or False'}
                                                        {questionData.question_type === 'fill_blank' && 'Students fill in the missing word(s)'}
                                                        {questionData.question_type === 'matching' && 'Students match items from two columns'}
                                                        {questionData.question_type === 'ordering' && 'Students arrange items in correct order'}
                                                        {questionData.question_type === 'essay' && 'Students write a free‑text response'}
                                                        {questionData.question_type === 'file_upload' && 'Students upload a file'}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Answers Section */}
                                            {['multiple_choice', 'single_choice', 'true_false', 'fill_blank', 'matching', 'ordering'].includes(questionData.question_type) && (
                                                <div className="mb-6">
                                                    <div className="flex justify-between items-center mb-3">
                                                        <h3 className="text-lg font-semibold text-gray-800">Answer Options</h3>
                                                        <button
                                                            type="button"
                                                            className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-2"
                                                            onClick={() => {
                                                                resetAnswerForm();
                                                                setShowAnswerModal(true);
                                                            }}
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                                                            </svg>
                                                            Add Option
                                                        </button>
                                                    </div>

                                                    {answers.length > 0 ? (
                                                        <div className="space-y-3">
                                                            {answers.map((answer, index) => (
                                                                <AnswerCard
                                                                    key={answer.answer_id}
                                                                    answer={answer}
                                                                    index={index}
                                                                    questionType={questionData.question_type}
                                                                    onEdit={() => {
                                                                        setEditingAnswer(answer);
                                                                        setNewAnswer({
                                                                            options: answer.options || "",
                                                                            answer_text: answer.answer_text || null,
                                                                            option_status: answer.option_status || "options",
                                                                            option_label: answer.option_label || "",
                                                                            partial_score: answer.partial_score || 0,
                                                                            sort_order: answer.sort_order || 0,
                                                                            match_type: answer.match_type || "exact",
                                                                            true_false_value: answer.true_false_value,
                                                                            match_pair: answer.match_pair || "",
                                                                            order_position: answer.order_position,
                                                                        });
                                                                        setShowAnswerModal(true);
                                                                    }}
                                                                    onDelete={() => {
                                                                        setCurrentAnswer(answer);
                                                                        setShowDeleteAnswerModal(true);
                                                                    }}
                                                                />
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-xl">
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
                                                            <p className="mt-2 text-gray-500">No answer options added yet</p>
                                                            <p className="text-sm text-gray-400">Add at least one correct answer</p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Explanation */}
                                            <div className="mb-6">
                                                <label className="block text-gray-700 font-semibold mb-3">
                                                    Explanation
                                                    <span className="text-sm text-gray-500 ml-2">(Optional - shown after test)</span>
                                                </label>
                                                <MyTextEditor
                                                    initialContent={questionData.explanation}
                                                    onChange={(value) => setQuestionData({ ...questionData, explanation: value })}
                                                    imageLibrary={dbImgFiles}
                                                    placeholder="Add explanation for this question..."
                                                    simple={true}
                                                />
                                            </div>

                                            {/* Reference */}
                                            <div className="mb-6">
                                                <label className="block text-gray-700 font-semibold mb-3">
                                                    Reference
                                                    <span className="text-sm text-gray-500 ml-2">(Optional - source or hint)</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                                    value={questionData.reference || ''}
                                                    onChange={(e) => setQuestionData({ ...questionData, reference: e.target.value })}
                                                    placeholder="e.g. Chapter 5, Page 123"
                                                />
                                            </div>

                                            {/* Save Buttons */}
                                            <div className="flex justify-end gap-3">
                                                <button
                                                    type="button"
                                                    className="px-5 py-2.5 bg-gray-200 text-gray-800 rounded-xl hover:bg-gray-300 transition-colors font-medium"
                                                    onClick={() => navigate(`/cbt_creator/cbt_data?cbt_data_id=${cbtDataPasscode}`)}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    className={`bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-2.5 px-8 rounded-xl font-semibold transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${saving ? 'opacity-75' : 'hover:scale-105'}`}
                                                    type="submit"
                                                    disabled={saving}
                                                >
                                                    {saving ? (
                                                        <>
                                                            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            Saving...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                            </svg>
                                                            Save Changes
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>

                            {/* Sidebar: Question Info & Quick Actions */}
                            <div className="space-y-8">
                                <QuestionInfoCard
                                    questionData={questionData}
                                    onConfigClick={() => setShowConfigModal(true)}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Configuration Modal */}
            <ConfigurationModal
                show={showConfigModal}
                onHide={() => setShowConfigModal(false)}
                questionData={questionData}
                setQuestionData={setQuestionData}
                dbImgFiles={dbImgFiles}
            />

            {/* Answer Modal */}
            <AnswerModal
                show={showAnswerModal}
                onHide={() => {
                    setShowAnswerModal(false);
                    resetAnswerForm();
                }}
                newAnswer={newAnswer}
                setNewAnswer={setNewAnswer}
                editingAnswer={editingAnswer}
                onSubmit={handleAnswerSubmit}
                dbImgFiles={dbImgFiles}
                questionType={questionData.question_type}
                saving={saving}
            />

            {/* Delete Answer Confirmation */}
            <DeleteAnswerModal
                show={showDeleteAnswerModal}
                onHide={() => setShowDeleteAnswerModal(false)}
                answer={currentAnswer}
                onDelete={() => handleDeleteAnswer(currentAnswer?.answer_id)}
            />

            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </div>
    );
};

// ============================================================================
//  HELPER COMPONENTS
// ============================================================================

// ---------- QuestionInfoCard ----------
const QuestionInfoCard = ({ questionData, onConfigClick }) => (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
        <div className="p-6 border-b border-gray-100">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Question Info
            </h2>
        </div>
        <div className="p-6 space-y-4">
            <div className="flex justify-between items-center">
                <span className="text-gray-600">Status</span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    questionData.status === 'published'
                        ? 'bg-green-100 text-green-800'
                        : questionData.status === 'draft'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                }`}>
                    {questionData.status || 'draft'}
                </span>
            </div>
            <div className="flex justify-between items-center">
                <span className="text-gray-600">Score</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                    {questionData.score}
                </span>
            </div>
            <div className="flex justify-between items-center">
                <span className="text-gray-600">Max Options Select</span>
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">
                    {questionData.max_options_select}
                </span>
            </div>
            <div className="flex justify-between items-center">
                <span className="text-gray-600">Type</span>
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm font-medium capitalize">
                    {questionData.question_type?.replace('_', ' ') || 'multiple choice'}
                </span>
            </div>
            <div className="flex justify-between items-center">
                <span className="text-gray-600">Difficulty</span>
                <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium capitalize">
                    {questionData.difficulty || 'medium'}
                </span>
            </div>
            <div className="flex justify-between items-center">
                <span className="text-gray-600">Topic</span>
                <span className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm font-medium">
                    {questionData.topic || '—'}
                </span>
            </div>
            <div className="flex justify-between items-center">
                <span className="text-gray-600">Sub‑topic</span>
                <span className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm font-medium">
                    {questionData.sub_topic || '—'}
                </span>
            </div>
            <div className="flex justify-between items-center">
                <span className="text-gray-600">Partial Scoring</span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    questionData.partial_scoring ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                    {questionData.partial_scoring ? 'Yes' : 'No'}
                </span>
            </div>
            <div className="flex justify-between items-center">
                <span className="text-gray-600">Negative Marking</span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    questionData.negative_marking ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                }`}>
                    {questionData.negative_marking ? 'Yes' : 'No'}
                </span>
            </div>
            <button
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-xl font-medium transition-all duration-200 hover:scale-105 flex items-center justify-center gap-2 mt-4"
                onClick={onConfigClick}
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                </svg>
                Configure Settings
            </button>
        </div>
    </div>
);

// ---------- AnswerCard ----------
const AnswerCard = ({ answer, index, questionType, onEdit, onDelete }) => {
    const label = answer.option_label || String.fromCharCode(65 + index);

    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                    <span className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium ${
                        answer.option_status === 'correct' ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700'
                    }`}>
                        {label}
                    </span>
                    <div className="flex-1">
                        {answer.options ? (
                            <div className="prose prose-sm max-w-none mb-2" dangerouslySetInnerHTML={{ __html: answer.options }} />
                        ) : (
                            <div className="text-gray-800 mb-2">{answer.answer_text}</div>
                        )}
                        <div className="flex flex-wrap gap-2 mt-1 text-xs text-gray-500">
                            {answer.partial_score > 0 && (
                                <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                                    Partial: {answer.partial_score}
                                </span>
                            )}
                            {answer.sort_order > 0 && (
                                <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">
                                    Order: {answer.sort_order}
                                </span>
                            )}
                            {questionType === 'fill_blank' && answer.match_type && (
                                <span className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full">
                                    Match: {answer.match_type}
                                </span>
                            )}
                            {questionType === 'true_false' && answer.true_false_value !== undefined && (
                                <span className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full">
                                    Value: {answer.true_false_value ? 'True' : 'False'}
                                </span>
                            )}
                            {questionType === 'matching' && answer.match_pair && (
                                <span className="bg-pink-50 text-pink-700 px-2 py-0.5 rounded-full">
                                    Pair: {answer.match_pair}
                                </span>
                            )}
                            {questionType === 'ordering' && answer.order_position && (
                                <span className="bg-yellow-50 text-yellow-700 px-2 py-0.5 rounded-full">
                                    Position: {answer.order_position}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex gap-2 ml-4">
                    <button onClick={onEdit} className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                    </button>
                    <button onClick={onDelete} className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                    </button>
                </div>
            </div>
            {answer.option_status === 'correct' && (
                <div className="mt-2 flex items-center gap-1 text-green-600 text-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>Correct Answer</span>
                </div>
            )}
        </div>
    );
};

// ---------- ConfigurationModal ----------
const ConfigurationModal = ({ show, onHide, questionData, setQuestionData, dbImgFiles }) => {
    const [tempData, setTempData] = useState(questionData);

    useEffect(() => {
        setTempData(questionData);
    }, [questionData]);

    const handleSave = () => {
        setQuestionData(tempData);
        onHide();
        toast.success('Settings updated successfully!');
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered backdrop="static" keyboard={false}>
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-lg">
                <h3 className="text-xl font-bold flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Question Configuration
                </h3>
            </div>
            <div className="bg-white p-6" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <div className="space-y-6">

                    {/* Status */}
                    <div>
                        <label className="block text-gray-700 font-semibold mb-3">Question Status</label>
                        <select
                            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            value={tempData.status}
                            onChange={(e) => setTempData({ ...tempData, status: e.target.value })}
                        >
                            <option value="published">Published</option>
                            <option value="draft">Draft</option>
                            <option value="archived">Archived</option>
                        </select>
                    </div>

                    {/* Score & Max Options Select */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-gray-700 font-semibold mb-3">Score</label>
                            <input
                                type="number"
                                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                value={tempData.score}
                                onChange={(e) => setTempData({ ...tempData, score: parseFloat(e.target.value) || 0 })}
                                min="0"
                                step="0.5"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-semibold mb-3">
                                Max Options Select
                                <span className="text-sm text-gray-500 ml-2">(Multiple choice only)</span>
                            </label>
                            <input
                                type="number"
                                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                value={tempData.max_options_select}
                                onChange={(e) => setTempData({ ...tempData, max_options_select: parseInt(e.target.value) || 1 })}
                                min="1"
                                disabled={!['multiple_choice', 'single_choice'].includes(tempData.question_type)}
                            />
                        </div>
                    </div>

                    {/* Difficulty, Topic, Sub-topic */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                            <label className="block text-gray-700 font-semibold mb-3">Difficulty</label>
                            <select
                                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                value={tempData.difficulty}
                                onChange={(e) => setTempData({ ...tempData, difficulty: e.target.value })}
                            >
                                <option value="easy">Easy</option>
                                <option value="medium">Medium</option>
                                <option value="hard">Hard</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-gray-700 font-semibold mb-3">Topic</label>
                            <input
                                type="text"
                                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                value={tempData.topic || ''}
                                onChange={(e) => setTempData({ ...tempData, topic: e.target.value })}
                                placeholder="e.g. Algebra"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-semibold mb-3">Sub-topic</label>
                            <input
                                type="text"
                                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                value={tempData.sub_topic || ''}
                                onChange={(e) => setTempData({ ...tempData, sub_topic: e.target.value })}
                                placeholder="e.g. Quadratic Equations"
                            />
                        </div>
                    </div>

                    {/* Question Format */}
                    <div>
                        <label className="block text-gray-700 font-semibold mb-3">Question Format</label>
                        <select
                            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            value={tempData.question_format}
                            onChange={(e) => setTempData({ ...tempData, question_format: e.target.value })}
                        >
                            <option value="text">Text</option>
                            <option value="image">Image</option>
                            <option value="audio">Audio</option>
                            <option value="video">Video</option>
                            <option value="mixed">Mixed</option>
                        </select>
                    </div>

                    {/* Question type */}
                    <div>
                        <label className="block text-gray-700 font-semibold mb-3">Question Type</label>
                        <select
                            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            value={tempData.question_type}
                            onChange={(e) => setTempData({ ...tempData, question_type: e.target.value, correct_answer: '' })}
                        >
                            <option value="multiple_choice">Multiple Choice</option>
                            <option value="essay">Theory/Subjective</option>
                            {/* <option value="true_false">True/False</option> */}
                            {/* <option value="fill_blank">Fill in the Blank</option> */}
                        </select>
                    </div>

                    {/* Conditional correct answer for True/False and Fill in the Blank */}
                    {['true_false', 'fill_blank'].includes(tempData.question_type) && (
                        <div className="md:col-span-2">
                            <label className="block text-gray-700 font-medium mb-2">
                                {tempData.question_type === 'true_false'
                                    ? 'Correct Answer'
                                    : 'Correct Answer (Fill in the Blank)'}
                            </label>
                            {tempData.question_type === 'true_false' ? (
                                <div className="flex items-center gap-6">
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="radio"
                                            name="trueFalse"
                                            value="true"
                                            checked={tempData.correct_answer === 'true'}
                                            onChange={(e) => setTempData({ ...tempData, correct_answer: e.target.value })}
                                            className="h-4 w-4 text-blue-600"
                                        />
                                        <span>True</span>
                                    </label>
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="radio"
                                            name="trueFalse"
                                            value="false"
                                            checked={tempData.correct_answer === 'false'}
                                            onChange={(e) => setTempData({ ...tempData, correct_answer: e.target.value })}
                                            className="h-4 w-4 text-blue-600"
                                        />
                                        <span>False</span>
                                    </label>
                                </div>
                            ) : (
                                <input
                                    type="text"
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    value={tempData.correct_answer || ''}
                                    onChange={(e) => setTempData({ ...tempData, correct_answer: e.target.value })}
                                    placeholder="e.g. Paris"
                                />
                            )}
                        </div>
                    )}

                    {/* Boolean Toggles */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="flex items-center gap-3">
                            <input
                                type="checkbox"
                                id="partial_scoring"
                                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                                checked={tempData.partial_scoring}
                                onChange={(e) => setTempData({ ...tempData, partial_scoring: e.target.checked })}
                            />
                            <label htmlFor="partial_scoring" className="text-gray-700 font-medium">
                                Enable Partial Scoring
                            </label>
                        </div>
                        <div className="flex items-center gap-3">
                            <input
                                type="checkbox"
                                id="negative_marking"
                                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                                checked={tempData.negative_marking}
                                onChange={(e) => setTempData({ ...tempData, negative_marking: e.target.checked })}
                            />
                            <label htmlFor="negative_marking" className="text-gray-700 font-medium">
                                Enable Negative Marking
                            </label>
                        </div>
                        <div className="flex items-center gap-3">
                            <input
                                type="checkbox"
                                id="mandatory"
                                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                                checked={tempData.mandatory}
                                onChange={(e) => setTempData({ ...tempData, mandatory: e.target.checked })}
                            />
                            <label htmlFor="mandatory" className="text-gray-700 font-medium">
                                Mandatory Question
                            </label>
                        </div>
                        <div className="flex items-center gap-3">
                            <input
                                type="checkbox"
                                id="has_attachment"
                                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                                checked={tempData.has_attachment}
                                onChange={(e) => setTempData({ ...tempData, has_attachment: e.target.checked })}
                            />
                            <label htmlFor="has_attachment" className="text-gray-700 font-medium">
                                Has Attachment
                            </label>
                        </div>
                    </div>

                    {/* Sort Order & Display Sort */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-gray-700 font-semibold mb-3">Sort Order</label>
                            <input
                                type="number"
                                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                value={tempData.sort}
                                onChange={(e) => setTempData({ ...tempData, sort: parseInt(e.target.value) || 1 })}
                                min="1"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-semibold mb-3">Display Order</label>
                            <div className="flex gap-4">
                                <label className="flex items-center">
                                    <input
                                        type="radio"
                                        className="h-4 w-4 text-blue-600"
                                        checked={tempData.display_sort === true}
                                        onChange={() => setTempData({ ...tempData, display_sort: true })}
                                    />
                                    <span className="ml-2">Sequential</span>
                                </label>
                                <label className="flex items-center">
                                    <input
                                        type="radio"
                                        className="h-4 w-4 text-blue-600"
                                        checked={tempData.display_sort === false}
                                        onChange={() => setTempData({ ...tempData, display_sort: false })}
                                    />
                                    <span className="ml-2">Random</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Reference */}
                    <div>
                        <label className="block text-gray-700 font-semibold mb-3">Reference</label>
                        <input
                            type="text"
                            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            value={tempData.reference || ''}
                            onChange={(e) => setTempData({ ...tempData, reference: e.target.value })}
                            placeholder="e.g. Source or citation"
                        />
                    </div>
                </div>
            </div>
            <div className="bg-gray-50 p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                    className="px-5 py-2.5 bg-gray-200 text-gray-800 rounded-xl hover:bg-gray-300 transition-colors font-medium"
                    onClick={onHide}
                >
                    Cancel
                </button>
                <button
                    className="px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium"
                    onClick={handleSave}
                >
                    Save Changes
                </button>
            </div>
        </Modal>
    );
};

// ---------- AnswerModal ----------
const AnswerModal = ({ show, onHide, newAnswer, setNewAnswer, editingAnswer, onSubmit, dbImgFiles, questionType, saving }) => {
    const [showAdvanced, setShowAdvanced] = useState(false);

    const toggleCorrect = (e) => {
        setNewAnswer({
            ...newAnswer,
            option_status: e.target.checked ? 'correct' : 'options'
        });
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered backdrop="static" keyboard={false}>
            <div className="bg-gradient-to-r from-green-600 to-teal-600 text-white p-6 rounded-t-lg">
                <h3 className="text-xl font-bold flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {editingAnswer ? 'Edit Answer' : 'Add New Answer'}
                </h3>
            </div>
            <div className="bg-white p-6" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <div className="space-y-6">
                    {/* Answer Content */}
                    <div>
                        <label className="block text-gray-700 font-semibold mb-3">
                            {questionType === 'fill_blank' ? 'Correct Answer Text' : 'Answer Text'}
                        </label>
                        {questionType === 'fill_blank' || questionType === 'essay' || questionType === 'theory' ? (
                            <MyTextEditor
                                initialContent={newAnswer.answer_text || ''}
                                onChange={(value) => setNewAnswer({ ...newAnswer, answer_text: value, options: null })}
                                imageLibrary={dbImgFiles}
                                placeholder="Enter answer text..."
                                simple={true}
                            />
                        ) : (
                            <MyTextEditor
                                initialContent={newAnswer.options || ''}
                                onChange={(value) => setNewAnswer({ ...newAnswer, options: value, answer_text: null })}
                                imageLibrary={dbImgFiles}
                                placeholder="Enter answer text..."
                                simple={true}
                            />
                        )}
                    </div>

                    {/* Correct / Incorrect Toggle */}
                    <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                className="w-5 h-5 text-green-600 rounded focus:ring-green-500"
                                checked={newAnswer.option_status === 'correct'}
                                onChange={toggleCorrect}
                            />
                            <span className="text-gray-700 font-medium">Mark as Correct Answer</span>
                        </label>
                    </div>

                    {/* Basic Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-gray-700 font-medium mb-2">Option Label (A, B, C...)</label>
                            <input
                                type="text"
                                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500"
                                value={newAnswer.option_label || ''}
                                onChange={(e) => setNewAnswer({ ...newAnswer, option_label: e.target.value })}
                                placeholder="e.g. A"
                                maxLength="10"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-medium mb-2">Sort Order</label>
                            <input
                                type="number"
                                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500"
                                value={newAnswer.sort_order || 0}
                                onChange={(e) => setNewAnswer({ ...newAnswer, sort_order: parseInt(e.target.value) || 0 })}
                                min="0"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-medium mb-2">Partial Score (if any)</label>
                            <input
                                type="number"
                                step="0.1"
                                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500"
                                value={newAnswer.partial_score || 0}
                                onChange={(e) => setNewAnswer({ ...newAnswer, partial_score: parseFloat(e.target.value) || 0 })}
                                min="0"
                            />
                        </div>
                    </div>

                    {/* Advanced Options Toggle */}
                    <div>
                        <button
                            type="button"
                            className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                            onClick={() => setShowAdvanced(!showAdvanced)}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 transition-transform ${showAdvanced ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            {showAdvanced ? 'Hide Advanced Options' : 'Show Advanced Options'}
                        </button>
                    </div>

                    {/* Advanced Fields */}
                    {showAdvanced && (
                        <div className="border-t border-gray-200 pt-6 mt-2 space-y-6">
                            {questionType === 'fill_blank' && (
                                <div>
                                    <label className="block text-gray-700 font-medium mb-2">Match Type</label>
                                    <select
                                        className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500"
                                        value={newAnswer.match_type || 'exact'}
                                        onChange={(e) => setNewAnswer({ ...newAnswer, match_type: e.target.value })}
                                    >
                                        <option value="exact">Exact Match</option>
                                        <option value="contains">Contains</option>
                                        <option value="regex">Regular Expression</option>
                                    </select>
                                </div>
                            )}

                            {questionType === 'true_false' && (
                                <div>
                                    <label className="block text-gray-700 font-medium mb-2">True/False Value</label>
                                    <div className="flex gap-4">
                                        <label className="flex items-center gap-2">
                                            <input
                                                type="radio"
                                                name="true_false"
                                                value="true"
                                                checked={newAnswer.true_false_value === true}
                                                onChange={() => setNewAnswer({ ...newAnswer, true_false_value: true })}
                                                className="w-4 h-4 text-green-600"
                                            />
                                            <span>True</span>
                                        </label>
                                        <label className="flex items-center gap-2">
                                            <input
                                                type="radio"
                                                name="true_false"
                                                value="false"
                                                checked={newAnswer.true_false_value === false}
                                                onChange={() => setNewAnswer({ ...newAnswer, true_false_value: false })}
                                                className="w-4 h-4 text-green-600"
                                            />
                                            <span>False</span>
                                        </label>
                                    </div>
                                </div>
                            )}

                            {questionType === 'matching' && (
                                <div>
                                    <label className="block text-gray-700 font-medium mb-2">Match Pair (right side)</label>
                                    <input
                                        type="text"
                                        className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500"
                                        value={newAnswer.match_pair || ''}
                                        onChange={(e) => setNewAnswer({ ...newAnswer, match_pair: e.target.value })}
                                        placeholder="Corresponding item"
                                    />
                                </div>
                            )}

                            {questionType === 'ordering' && (
                                <div>
                                    <label className="block text-gray-700 font-medium mb-2">Order Position</label>
                                    <input
                                        type="number"
                                        className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500"
                                        value={newAnswer.order_position || 1}
                                        onChange={(e) => setNewAnswer({ ...newAnswer, order_position: parseInt(e.target.value) || 1 })}
                                        min="1"
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <div className="bg-gray-50 p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                    className="px-5 py-2.5 bg-gray-200 text-gray-800 rounded-xl hover:bg-gray-300 transition-colors font-medium"
                    onClick={onHide}
                    disabled={saving}
                >
                    Cancel
                </button>
                <button
                    className="px-5 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    onClick={onSubmit}
                    disabled={saving}
                >
                    {saving ? (
                        <>
                            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {editingAnswer ? 'Updating...' : 'Adding...'}
                        </>
                    ) : (
                        <>{editingAnswer ? 'Update Answer' : 'Add Answer'}</>
                    )}
                </button>
            </div>
        </Modal>
    );
};

// ---------- DeleteAnswerModal ----------
const DeleteAnswerModal = ({ show, onHide, answer, onDelete }) => (
    <Modal show={show} onHide={onHide} size="md" centered backdrop="static" keyboard={false}>
        <div className="bg-gradient-to-r from-red-600 to-pink-600 text-white p-6 rounded-t-lg">
            <h3 className="text-xl font-bold flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Confirm Deletion
            </h3>
        </div>
        <div className="bg-white p-6">
            <div className="space-y-6">
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-xl">
                    <p className="text-red-700 font-medium">
                        Are you sure you want to delete this answer option? This action cannot be undone.
                    </p>
                </div>

                {answer && (
                    <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                        {answer.options ? (
                            <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: answer.options }} />
                        ) : (
                            <div className="text-gray-800">{answer.answer_text}</div>
                        )}
                        {answer.option_status === 'correct' && (
                            <div className="mt-2 flex items-center gap-1 text-green-600 text-sm">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                <span>This is marked as the correct answer</span>
                            </div>
                        )}
                    </div>
                )}

                <div className="flex justify-end gap-3">
                    <button
                        onClick={onHide}
                        className="px-5 py-2.5 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl transition-colors font-medium"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onDelete}
                        className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors font-medium flex items-center gap-2"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete Permanently
                    </button>
                </div>
            </div>
        </div>
    </Modal>
);

export { CbtQuestionEdit };